import { useCallback, useMemo, useRef, useState } from 'react';
import { REORDER_ERROR_MESSAGES, saveReorder } from '../../services/matchingApi.js';
import { readScoreOverride, withScoreOverride } from './scoreOverride';

// BTN-M-004 の横の文 (SCR-M-001 §4.3 BTN-M-004 の ①・③〜⑥。② は保存の失敗の文言で、別に出す)
export const REORDER_STATUS_MESSAGES = {
    saving: '保存中...', // ①
    finishScoreEdit: 'マッチ度の編集を終えてから保存してください。', // ③
    beforeSave: '保存すると、マッチ度も上から高い順に付け直します。', // ④
    justSaved:
        '並べ替えを保存し、マッチ度を上から高い順に付け直しました。'
        + 'AI の値と異なる数字には「手入力」と表示されます。', // ⑤
    saved: '並べ替え結果は保存済みです', // ⑥
};

/**
 * BTN-M-004 の横の文 (role="status" で出すもの) を決める。当てはまるもののうち番号の小さいもの (強いもの)
 * を返す (§4.3 BTN-M-004)。前回の保存が失敗した後 (②) は、失敗の文言を role="alert" で出すため null を返す。
 *
 * @param {{saving: boolean, failed: boolean, dirty: boolean, scoreEditing: boolean, justSaved: boolean}} state
 * @returns {string|null}
 */
export function reorderStatusText({ saving, failed, dirty, scoreEditing, justSaved }) {
    if (saving) return REORDER_STATUS_MESSAGES.saving;
    if (failed) return null;
    if (dirty) return scoreEditing ? REORDER_STATUS_MESSAGES.finishScoreEdit : REORDER_STATUS_MESSAGES.beforeSave;
    return justSaved ? REORDER_STATUS_MESSAGES.justSaved : REORDER_STATUS_MESSAGES.saved;
}

/**
 * 応答の推薦結果の要素で、推薦の要素 1 件のマッチ度 (AI の値と、手で直した値の有無) を入れ替えた
 * 新しい要素を返す。応答に同じ求人の要素が無ければ元の要素のまま。マッチ度以外の値 (並び・IN-M-011・
 * おすすめポイントの文) は入れ替えない (SCR-M-001 §6.2「並べ替えの保存でマッチ度を付け直す」手順 4)。
 *
 * @param {object} rec 推薦の要素 (推薦結果の recommendations、または画面の並びの要素)
 * @param {Map<string, object>} savedById 応答の要素を求人 ID で引く表
 * @returns {object}
 */
export function withRenumberedScore(rec, savedById) {
    const saved = savedById.get(rec.job_pool_item_id);
    if (!saved) return rec;
    const replaced = withScoreOverride(rec, readScoreOverride(saved));
    return typeof saved.score === 'number' ? { ...replaced, score: saved.score } : replaced;
}

/**
 * 並べ替えの保存 (BTN-M-004) の状態と保存。保存できたら、応答の推薦結果でマッチ度を入れ替える
 * (画面レイアウト_SCR-M-001.md §6.2「並べ替えの保存でマッチ度を付け直す」、Issue #443・#2173)。
 *
 * useAiMatching から呼ぶ (useAiMatching.js を大きくしないために分けた)。
 *
 * 保存中・失敗の文言・保存できた直後は、どの推薦結果に対するものかを一緒に持つ。保存の間に表示する
 * 結果が替わったとき (保存中に実行した等)、前の結果の状態と応答の値を新しい結果に入れないため (手順 6)。
 *
 * @param {object|null} matchingResult 表示中の推薦結果
 * @param {Function} setMatchingResult
 * @param {object[]} orderedItems 画面の並び
 * @param {Function} setOrderedItems
 * @param {string[]|null} savedOrderIds 保存済みの並び (表示したとき、または前回保存したときの並び)
 * @param {Function} setSavedOrderIds
 * @param {{current: boolean}|null} otherSavingRef マッチ度・求職者に提案するか・おすすめポイントの文の
 *   どれかの保存中か。その間は保存を呼ばない (§6.2「同時に保存するもの」。押した時点の値で判定する)
 * @param {{current: boolean}} savingRef 本フックの保存中を書き込む ref。ほかの保存のフックが押した時点で読む
 * @param {boolean} scoreEditing マッチ度の数字の編集中か。その間は保存を呼ばない (§4.3 BTN-M-004)
 */
function useReorderSave(
    matchingResult,
    setMatchingResult,
    orderedItems,
    setOrderedItems,
    savedOrderIds,
    setSavedOrderIds,
    otherSavingRef,
    savingRef,
    scoreEditing
) {
    const shownResultId = matchingResult?.id ?? null;
    // 応答を受け取った時点で表示中の結果を知るために持つ (useScoreAdjustments.js と同じ)
    const shownResultIdRef = useRef(shownResultId);
    shownResultIdRef.current = shownResultId;

    // 保存中の結果 ({ resultId })・失敗の文言 ({ resultId, message })・保存できた直後 ({ resultId })
    const [saving, setSaving] = useState(null);
    const [failure, setFailure] = useState(null);
    const [justSaved, setJustSaved] = useState(null);

    const forShown = (state) => (state && state.resultId === shownResultId ? state : null);

    const isReorderDirty = useMemo(() => {
        if (!savedOrderIds) return false;
        const currentIds = orderedItems.map((item) => item.job_pool_item_id);
        return currentIds.some((id, i) => id !== savedOrderIds[i]);
    }, [orderedItems, savedOrderIds]);

    // 並びを動かしたら「保存できた直後」の文を消す (§4.3 BTN-M-004 ⑤)。
    // 失敗の文言は消さない (消す時機は、もう一度押したときと表示する推薦結果が替わったとき。同 ②)
    const clearJustSaved = useCallback(() => setJustSaved(null), []);
    // AI マッチングを実行したときに、失敗の文言と「保存できた直後」の文を消す (useAiMatching の実行が呼ぶ)
    const clearMessages = useCallback(() => {
        setFailure(null);
        setJustSaved(null);
    }, []);

    const saveReorderChanges = useCallback(async () => {
        if (!matchingResult || !isReorderDirty || scoreEditing) return;
        if (savingRef.current || otherSavingRef?.current) return;
        const targetId = matchingResult.id;
        const orderedIds = orderedItems.map((item) => item.job_pool_item_id);
        savingRef.current = true;
        setSaving({ resultId: targetId });
        setFailure(null);
        setJustSaved(null);

        // 保存中の印は finally で戻す (戻らないと、以後すべての保存が押せなくなり、エラーとしても見えない)
        try {
            let result;
            try {
                result = await saveReorder(targetId, orderedIds);
            } catch (err) {
                // 失敗は saveReorder が結果で返す。ここに来るのは想定外の例外だけ
                console.error('useReorderSave: 並べ替えの保存で例外が発生しました', err);
                result = { ok: false, errorMessage: REORDER_ERROR_MESSAGES.network };
            }

            if (!result.ok) {
                setFailure({ resultId: targetId, message: result.errorMessage });
                return;
            }
            const savedById = new Map(result.recommendations.map((rec) => [rec.job_pool_item_id, rec]));
            const applySaved = (rec) => withRenumberedScore(rec, savedById);
            // 保存の間に別の結果へ替わっていたら入れない。並びは応答で置き換えない (手順 4)
            setMatchingResult((prev) =>
                prev && prev.id === targetId && Array.isArray(prev.recommendations)
                    ? { ...prev, recommendations: prev.recommendations.map(applySaved) }
                    : prev
            );
            if (shownResultIdRef.current === targetId) {
                setOrderedItems((prev) => prev.map(applySaved));
                setSavedOrderIds(orderedIds);
                setJustSaved({ resultId: targetId });
            }
        } finally {
            savingRef.current = false;
            setSaving(null);
        }
    }, [
        matchingResult,
        isReorderDirty,
        scoreEditing,
        savingRef,
        otherSavingRef,
        orderedItems,
        setMatchingResult,
        setOrderedItems,
        setSavedOrderIds,
    ]);

    const failureForShown = forShown(failure);

    return {
        isReorderDirty,
        // どの結果の保存でも真 (保存を重ねて送らない。ほかの保存のフックと同じ)
        reorderSaving: saving !== null,
        reorderError: failureForShown ? failureForShown.message : null,
        reorderJustSaved: forShown(justSaved) !== null,
        clearReorderJustSaved: clearJustSaved,
        clearReorderMessages: clearMessages,
        saveReorderChanges,
    };
}

export default useReorderSave;
